import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { MAX_IMAGES_PER_ITEM, PRICING_MODES, TITLE_MAX, itemTitle, priceLabel, type AdminItem, type ItemCondition, type PricingMode } from '@sellit/shared';
import { api, ApiError } from '../lib/api';
import { setAppTitle, storage } from '../lib/format';
import { Icon } from '../lib/Icon';
import { publicUrl, ShareButton } from '../lib/Share';
import { PriceEntry, priceValueFrom, readPrice, type PriceValue } from './PriceEntry';
import { ConditionSelect } from './ConditionSelect';
import { takeFiles, usePhotoUploads, type Photo } from './usePhotoUploads';

interface Draft extends PriceValue {
  title: string;
  description: string;
  condition: ItemCondition | null;
}

const draftKey = (saleId: string) => `sellit.draft.${saleId}`;
// A run of free (or best-offer) items keeps that choice for the next one; the price box starts empty.
const lastMode = (): PricingMode => {
  const m = storage.get('sellit.pricingMode');
  return (PRICING_MODES as readonly string[]).includes(m ?? '') ? (m as PricingMode) : 'FIXED';
};
const emptyDraft = (): Draft => ({ title: '', description: '', condition: null, ...priceValueFrom(lastMode(), null) });

function loadDraft(saleId: string): Draft {
  try {
    const raw = storage.get(draftKey(saleId));
    if (raw) {
      // Drafts saved before price-first entry carry a `mode` instead of the two toggles.
      const saved = JSON.parse(raw) as Partial<Draft> & { mode?: PricingMode };
      const legacy = saved.mode && saved.free === undefined ? { free: saved.mode === 'FREE', offers: saved.mode === 'MAKE_OFFER' } : {};
      return { ...emptyDraft(), ...saved, ...legacy };
    }
  } catch {
    /* corrupt draft */
  }
  return emptyDraft();
}

export function AddItemPage() {
  const { saleId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [draft, setDraft] = useState<Draft>(() => loadDraft(saleId));
  const uploads = usePhotoUploads(saleId);
  const { photos, addFiles, uploading, failed } = uploads;
  const [showDetails, setShowDetails] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [waitingToPublish, setWaitingToPublish] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [published, setPublished] = useState<AdminItem | null>(null);
  const [saleSlug, setSaleSlug] = useState<string | null>(null);

  useEffect(() => setAppTitle('Add item'), []);
  useEffect(() => {
    api.sale(saleId).then((s) => setSaleSlug(s.slug), () => undefined);
  }, [saleId]);

  // Keep typed text across reloads and dropped signal; cleared on publish.
  useEffect(() => {
    if (!published) storage.set(draftKey(saleId), JSON.stringify(draft));
  }, [draft, saleId, published]);

  // Photos taken from the dashboard's Add Item button arrive in router state.
  const handedOver = useRef(false);
  useEffect(() => {
    const files = (location.state as { files?: File[] } | null)?.files;
    if (!handedOver.current && files?.length) {
      handedOver.current = true;
      addFiles(files);
      navigate('.', { replace: true, state: null });
    }
  }, [location.state, addFiles, navigate]);

  function onFiles(e: ChangeEvent<HTMLInputElement>) {
    const files = takeFiles(e);
    if (published && files.length) startNext();
    if (files.length) addFiles(files);
  }

  function startNext() {
    uploads.reset();
    setDraft(emptyDraft());
    setShowDetails(false);
    setPublished(null);
    setError(null);
    setFieldErrors({});
  }

  const { pricing } = readPrice(draft);
  const ready = photos.length > 0 && pricing !== null && !failed;

  const publish = useCallback(async () => {
    if (!pricing) return;
    setPublishing(true);
    setError(null);
    try {
      const item = await api.createItem(saleId, {
        title: draft.title,
        description: draft.description,
        condition: draft.condition,
        ...pricing,
        imageIds: uploads.imageIds(),
      });
      storage.remove(draftKey(saleId));
      storage.set('sellit.pricingMode', pricing.pricingMode);
      setPublished(item);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not publish. Try again.');
      if (err instanceof ApiError && err.fields) setFieldErrors(err.fields);
    } finally {
      setPublishing(false);
      setWaitingToPublish(false);
    }
  }, [draft, uploads, pricing, saleId]);

  // "Publish" tapped while photos were still uploading: finish as soon as they're in.
  useEffect(() => {
    if (waitingToPublish && !uploading && !publishing) {
      if (ready) void publish();
      else setWaitingToPublish(false);
    }
  }, [waitingToPublish, uploading, publishing, ready, publish]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!ready || publishing) return;
    if (uploading) setWaitingToPublish(true);
    else void publish();
  }

  if (published) {
    return (
      <main className="page narrow">
        <div className="success">
          <p className="success-mark"><Icon name="check" size={34} /></p>
          <h1>Posted #{published.number}</h1>
          <p>{itemTitle(published)} · {published.priceLabel}</p>
          <p className="muted small">It's live on your sale page.</p>
        </div>
        <div className="stack">
          <label className="btn primary big">
            <Icon name="camera" size={22} /> Add Another Item
            <input type="file" accept="image/*" capture="environment" hidden onChange={onFiles} />
          </label>
          {saleSlug && (
            <ShareButton title={`${itemTitle(published)} · ${published.priceLabel}`} url={publicUrl(saleSlug, published.number)} label="Share This Item" />
          )}
          <label className="btn">
            <Icon name="image" /> Next item from your library
            <input type="file" accept="image/*" multiple hidden onChange={onFiles} />
          </label>
          <Link className="link center-link" to={`/admin/sales/${saleId}`}>Back to my sale</Link>
        </div>
      </main>
    );
  }

  const cover = photos[0];
  const extras = photos.slice(1);

  return (
    <main className="page narrow">
      <header className="admin-header">
        <h1>Add an item</h1>
        <Link className="link small" to={`/admin/sales/${saleId}`}>Cancel</Link>
      </header>

      <form onSubmit={onSubmit} className="steps">
        <section className="step" aria-labelledby="step1">
          <h2 id="step1"><span className="step-num">1</span> Take a picture</h2>
          {cover ? (
            <>
              <CoverPreview photo={cover} onRetry={() => uploads.retry(cover)} />
              <label className="btn">
                <Icon name="camera" /> Replace photo
                <input type="file" accept="image/*" hidden onChange={(e) => { const [f] = takeFiles(e); if (f) uploads.replaceCover(f); }} />
              </label>
            </>
          ) : (
            <>
              <label className="photo-drop">
                <Icon name="camera" size={40} />
                <strong>Tap to take a photo</strong>
                <input type="file" accept="image/*" capture="environment" hidden onChange={onFiles} />
              </label>
              <label className="btn">
                <Icon name="image" /> Choose from library
                <input type="file" accept="image/*" multiple hidden onChange={onFiles} />
              </label>
            </>
          )}
          {fieldErrors.imageIds && <p className="error small">{fieldErrors.imageIds}</p>}
        </section>

        <section className="step step-price" aria-labelledby="step2">
          <h2 id="step2"><span className="step-num">2</span> Set a price</h2>
          <PriceEntry value={draft} onChange={(v) => setDraft({ ...draft, ...v })} error={fieldErrors.priceCents} />
        </section>

        <section className="step" aria-labelledby="step3">
          <h2 id="step3"><span className="step-num">3</span> Post it</h2>
          {error && <p className="error" role="alert">{error}</p>}
          <button className="btn primary big" disabled={!ready || publishing || waitingToPublish}>
            {publishing
              ? 'Posting…'
              : waitingToPublish
                ? 'Posting when the photo finishes…'
                : ready && pricing
                  ? `Post My Item · ${priceLabel(pricing.pricingMode, pricing.priceCents)}`
                  : 'Post My Item'}
          </button>
          {!ready && !publishing && (
            <p className="muted small">{!cover ? 'Add a photo and a price to post.' : !pricing ? 'Add a price to post.' : uploads.failed ? 'A photo didn\'t upload. Tap Retry.' : ''}</p>
          )}
        </section>

        <details className="more-details" open={showDetails} onToggle={(e) => setShowDetails(e.currentTarget.open)}>
          <summary>More Details (Optional)</summary>
          <div className="stack">
            <label className="field">
              <span>Item name</span>
              <input
                value={draft.title}
                maxLength={TITLE_MAX}
                autoCapitalize="sentences"
                enterKeyHint="done"
                placeholder="e.g. Oak dresser"
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              />
              {fieldErrors.title && <small className="error">{fieldErrors.title}</small>}
            </label>
            <label className="field">
              <span>Description</span>
              <textarea rows={3} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            </label>
            <ConditionSelect value={draft.condition} onChange={(condition) => setDraft({ ...draft, condition })} />
            <ExtraPhotos photos={extras} uploads={uploads} canAdd={cover !== undefined && photos.length < MAX_IMAGES_PER_ITEM} />
            <p className="muted small">Tip: keep house numbers and mail out of your photos.</p>
          </div>
        </details>
      </form>
    </main>
  );
}

function CoverPreview({ photo, onRetry }: { photo: Photo; onRetry: () => void }) {
  return (
    <div className={`cover-preview photo ${photo.status}`}>
      <img src={photo.previewUrl} alt="Your item" />
      {photo.status === 'uploading' && <span className="progress" style={{ width: `${Math.round(photo.progress * 100)}%` }} />}
      {photo.status === 'error' && (
        <button type="button" className="photo-error" onClick={onRetry} title={photo.error}>Retry</button>
      )}
    </div>
  );
}

function ExtraPhotos({ photos, uploads, canAdd }: { photos: Photo[]; uploads: ReturnType<typeof usePhotoUploads>; canAdd: boolean }) {
  return (
    <div className="field">
      <span>More photos</span>
      {photos.length > 0 && (
        <div className="photo-grid">
          {photos.map((p) => (
            <div key={p.key} className={`photo ${p.status}`}>
              <img src={p.previewUrl} alt="" />
              {p.status === 'uploading' && <span className="progress" style={{ width: `${Math.round(p.progress * 100)}%` }} />}
              {p.status === 'error' && <button type="button" className="photo-error" onClick={() => uploads.retry(p)} title={p.error}>Retry</button>}
              <button type="button" className="photo-cover button" onClick={() => uploads.makeCover(p)}>Make main</button>
              <button type="button" className="photo-remove" aria-label="Remove photo" onClick={() => uploads.remove(p)}>×</button>
            </div>
          ))}
        </div>
      )}
      {canAdd ? (
        <label className="btn">
          <Icon name="image" /> Add more photos
          <input type="file" accept="image/*" multiple hidden onChange={(e) => uploads.addFiles(takeFiles(e))} />
        </label>
      ) : (
        <small className="muted">{photos.length ? '' : 'Add the main photo first.'}</small>
      )}
    </div>
  );
}
